/* Copyright (c) 2020-present CloudIO Inc */

import { Slider, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { constants, Height, HeightProp } from 'componentregistry';
import SelectInput from 'custom/SelectInput';
import React, { memo } from 'react';
import { getDefaultValues } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const { heightType } = constants;

const options = (Object.keys(heightType) as (keyof typeof heightType)[]).map(
  (k) => {
    return { value: k, label: heightType[k] };
  },
);

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 8,
    position: 'relative',
  },
  color: {
    marginLeft: 24,
  },
  inner: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    backgroundColor: theme.palette.background.default,
    borderRadius: 4,
    position: 'relative',
  },
  label: {
    flex: 0,
    minWidth: 80,
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
  option: {
    alignSelf: 'center',
    border: '1px solid transparent',
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    flex: 1,
    fontSize: '0.8rem',
    outline: 'none',
    overflow: 'hidden',
    padding: '4px 0',
    position: 'relative',
    textAlign: 'center',
    textOverflow: 'ellipsis',
    transition: 'color 0.2s',
    '& > svg': {
      height: '1em',
      width: '1em',
    },
    '&:not(:last-child)::after': {
      content: '""',
      position: 'absolute',
      top: 8,
      bottom: 8,
      right: 0,
      width: 1,
      backgroundColor: theme.palette.divider,
    },
    '&:not(:first-of-type)': {
      marginLeft: '-1px',
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
  },
  selected: {},
  slider: {
    flex: 2,
    height: '30px',
    overflow: 'visible',
    padding: '0 8px',
    zIndex: 1,
  },
  expander: {
    margin: '0 4px',
    outline: 'none',
    width: 14,
    '& > svg': {
      fill: theme.palette.text.secondary,
    },
    '&$selected': {
      '& > svg': {
        fill: theme.palette.primary.main,
      },
    },
    cursor: 'pointer',
  },
  right: {
    textAlign: 'right',
    paddingRight: 8,
  },
  input: {
    border: `4px solid ${theme.palette.background.default}`,
    lineHeight: '22px',
    padding: 0,
    textAlign: 'end',
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: '32px',
    width: '32px',
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
  },
  styleSelect: {
    width: 80,
  },
}));

function HeightProperty({
  activeItemId,
  pageId,
  property,
  mgr,
  value = getDefaultValues<Height>(mgr, property),
  onChange,
  isViewOnlyUser = false,
}: PropertyEditorProps<HeightProp, Height>) {
  const classes = useStyles();

  return (
    <div data-testid={property} className={classes.root}>
      <div className={clsx(classes.label)}>Height</div>
      <div className={classes.inner}>
        <div className={clsx(classes.option, classes.slider)}>
          <Slider
            disabled={value.type === 'auto' || isViewOnlyUser}
            valueLabelDisplay="auto"
            step={value.type === '%' ? 10 : 40}
            min={0}
            max={value.type === '%' ? 100 : 800}
            onChange={(_: Event, val: number | number[]) => {
              if (typeof val === 'number') {
                onChange(property, {
                  ...value,
                  value: val,
                  type: value.type ?? 'px',
                });
              }
            }}
            value={value?.value ?? 0}
          />
        </div>
        <input
          className={clsx(classes.option, classes.input)}
          value={value?.value ?? ''}
          disabled={value.type === 'auto' || isViewOnlyUser}
          onChange={(e) => {
            onChange(property, {
              ...value,
              value: e.target.value ? parseFloat(e.target.value) : 0,
            });
          }}
        />
        <SelectInput
          disabled={isViewOnlyUser}
          value={value?.type ?? 'auto'}
          options={options}
          onChange={(type) => {
            const val = type === 'auto' ? undefined : value?.value ?? 100;
            onChange(property, {
              ...value,
              type: type as Height['type'],
              value: val,
            });
          }}
          disableClearable
          className={classes.styleSelect}
          disableUnderline
          variant="standard"
        />
      </div>
    </div>
  );
}

export default memo(HeightProperty);
